// src/components/SuperAdmin/TaxonomyManager.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Settings,
  Tag,
  Building2,
  AlertCircle,
  Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TaxonomyManager = () => {
  const { superAdmin } = useSuperAdminAuth();
  const { toast } = useToast();

  // Create ONE API client and always inject the latest SuperAdmin token per request.
  // This avoids hard-depending on superAdmin?.token inside axios.create headers
  // and avoids gating the initial fetch on token.
  const apiRef = useRef(null);

  if (!apiRef.current) {
    const instance = axios.create({
      baseURL: BACKEND_URL,
    });

    instance.interceptors.request.use((config) => {
      // Use context token first; fallback to localStorage if your auth context hydrates async.
      const token =
        superAdmin?.token ||
        localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') ||
        localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');

      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    apiRef.current = instance;
  }

  const api = apiRef.current;

  const [industries, setIndustries] = useState([]);
  const [subSectors, setSubSectors] = useState([]);
  const [feedbackCategories, setFeedbackCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, industries, categories, mappings
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedSubSector, setSelectedSubSector] = useState(null);
  const [expandedIndustries, setExpandedIndustries] = useState(new Set());

  // Dialog states
  const [showAddIndustry, setShowAddIndustry] = useState(false);
  const [showAddSubSector, setShowAddSubSector] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);

  // Form states
  const [industryForm, setIndustryForm] = useState({ name: '', description: '', icon: '' });
  const [subSectorForm, setSubSectorForm] = useState({ industry_id: '', name: '', description: '' });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    default_priority_level: 'medium',
    ai_intent_keywords: '',
    is_universal: false,
    sub_sector_ids: [] // For associating with multiple sub-sectors
  });
  const [mappingForm, setMappingForm] = useState({
    sub_sector_id: '',
    feedback_category_id: '',
    escalation_level: 'operator',
    sla_hours: 24,
    auto_priority: 'medium',
    is_mandatory: true
  });

  const safeArray = (v) => (Array.isArray(v) ? v : []);

  // ===== TAX-GOV-2 Slice 2b — governed category lifecycle + propagation/reconcile control =====
  // gov.kind: 'propagation' (ADD to applicable tenants) | 'reconcile' (deactivate/reactivate/mapping change).
  // Flow is strictly Preview(dry-run) -> reviewed dry_run_hash -> Apply(hash). No one-click "sync all".
  const [gov, setGov] = useState({
    open: false, category: null, kind: null, loading: false, plan: null, applying: false, result: null,
  });
  const [togglingId, setTogglingId] = useState(null);
  // Edit (metadata) is DISTINCT from Deactivate/Reactivate (lifecycle) and from propagation/reconcile.
  const [editCat, setEditCat] = useState({ open: false, id: null, name: '', description: '', default_priority_level: 'medium', ai_intent_keywords: '', is_universal: false, saving: false });

  const catActive = (c) => c && c.is_active !== false; // absent => active

  const openEditCategory = (category) => setEditCat({
    open: true, id: category.id, name: category.name || '', description: category.description || '',
    default_priority_level: category.default_priority_level || 'medium',
    ai_intent_keywords: safeArray(category.ai_intent_keywords).join(', '),
    is_universal: !!category.is_universal, saving: false,
  });

  // Saves ONLY approved global metadata via the canonical PUT. Never sends is_active (that is the lifecycle
  // action) and never writes tenant projections — applicability changes go through governed reconcile.
  const saveEditCategory = async () => {
    setEditCat((s) => ({ ...s, saving: true }));
    try {
      const payload = {
        name: editCat.name,
        description: editCat.description,
        default_priority_level: editCat.default_priority_level,
        ai_intent_keywords: String(editCat.ai_intent_keywords || '').split(',').map((k) => k.trim()).filter(Boolean),
        is_universal: editCat.is_universal,
      };
      await api.put(`/api/v1/superadmin/taxonomy/feedback-categories/${editCat.id}`, payload);
      toast({ title: 'Category updated', description: 'Global category metadata saved. Applicability changes propagate via governed reconciliation.' });
      setEditCat((s) => ({ ...s, open: false, saving: false }));
      await fetchAllData();
    } catch (e) {
      setEditCat((s) => ({ ...s, saving: false }));
      toast({ title: 'Save failed', description: (e?.response?.data?.detail) || 'Could not update category', variant: 'destructive' });
    }
  };

  // Deactivate / Reactivate a GLOBAL category. Updates the global authority ONLY (backend performs NO tenant
  // write) — the SuperAdmin then runs governed reconcile to propagate. Never a delete.
  const toggleCategoryActive = async (category) => {
    const next = !catActive(category);
    setTogglingId(category.id);
    try {
      await api.put(`/api/v1/superadmin/taxonomy/feedback-categories/${category.id}`, { is_active: next });
      toast({
        title: next ? 'Category reactivated' : 'Category deactivated',
        description: next
          ? 'Global category is active again. Run “Preview reconciliation” to restore tenant availability.'
          : 'Global category is inactive. Run “Preview reconciliation” to reconcile tenant availability. Tenant-local settings and history are preserved.',
      });
      await fetchAllData();
    } catch (e) {
      toast({ title: 'Update failed', description: (e?.response?.data?.detail) || 'Could not update category state', variant: 'destructive' });
    } finally {
      setTogglingId(null);
    }
  };

  const openGovPreview = async (category, kind) => {
    setGov({ open: true, category, kind, loading: true, plan: null, applying: false, result: null });
    try {
      const path = kind === 'propagation'
        ? `/api/v1/superadmin/taxonomy/feedback-categories/${category.id}/propagation/dry-run`
        : `/api/v1/superadmin/taxonomy/feedback-categories/${category.id}/reconcile/dry-run`;
      const resp = await api.get(path);
      setGov((g) => ({ ...g, loading: false, plan: resp.data || null }));
    } catch (e) {
      setGov((g) => ({ ...g, loading: false, plan: { error: (e?.response?.data?.detail) || 'dry-run failed' } }));
    }
  };

  // Apply is enabled ONLY after a successful dry-run produced a hash (UI_APPLY_WITHOUT_DRY_RUN=NO).
  const applyGovPlan = async () => {
    const { category, kind, plan } = gov;
    if (!plan || !plan.dry_run_hash) return;
    setGov((g) => ({ ...g, applying: true }));
    try {
      const path = kind === 'propagation'
        ? `/api/v1/superadmin/taxonomy/feedback-categories/${category.id}/propagation/apply`
        : `/api/v1/superadmin/taxonomy/feedback-categories/${category.id}/reconcile/apply`;
      const resp = await api.post(path, { dry_run_hash: plan.dry_run_hash });
      setGov((g) => ({ ...g, applying: false, result: resp.data || null }));
      await fetchAllData();
    } catch (e) {
      setGov((g) => ({ ...g, applying: false, result: { result: 'FAILED', reason: (e?.response?.data?.detail) || 'apply failed' } }));
    }
  };

  // Shared governance block rendered inside every category card (universal + sector).
  const renderCatGov = (category) => {
    const active = catActive(category);
    return (
      <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-2" data-testid={`catgov-${category.id}`}>
        <Badge variant={active ? 'outline' : 'destructive'} data-testid={`catstate-${category.id}`}>
          {active ? 'Active' : 'Inactive'}
        </Badge>
        <Badge variant="outline">{category.is_universal ? 'Universal' : 'Sector-specific'}</Badge>
        <Button size="sm" variant="outline" onClick={() => openEditCategory(category)} data-testid={`catedit-${category.id}`}>
          <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
        </Button>
        <Button
          size="sm"
          variant={active ? 'outline' : 'default'}
          disabled={togglingId === category.id}
          onClick={() => toggleCategoryActive(category)}
          data-testid={`cattoggle-${category.id}`}
        >
          {active ? 'Deactivate' : 'Reactivate'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => openGovPreview(category, 'propagation')} data-testid={`catprop-${category.id}`}>
          Preview propagation
        </Button>
        <Button size="sm" variant="outline" onClick={() => openGovPreview(category, 'reconcile')} data-testid={`catrecon-${category.id}`}>
          Preview reconciliation
        </Button>
      </div>
    );
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [indResp, catResp] = await Promise.all([
        api.get('/api/v1/superadmin/taxonomy/industries'),
        // active_only=false so SuperAdmin sees INACTIVE (deactivated) categories too and can reactivate them
        api.get('/api/v1/superadmin/taxonomy/feedback-categories?active_only=false')
      ]);

      const industriesData = safeArray(indResp.data);
      const categoriesData = safeArray(catResp.data);

      setIndustries(industriesData);
      setFeedbackCategories(categoriesData);

      // Fetch all sub-sectors
      const allSubSectors = [];
      for (const industry of industriesData) {
        const subResp = await api.get(`/api/v1/superadmin/taxonomy/sub-sectors?industry_id=${industry.id}`);
        allSubSectors.push(...safeArray(subResp.data));
      }
      setSubSectors(allSubSectors);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching taxonomy data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load taxonomy data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMappingsForSubSector = async (subSectorId) => {
    try {
      const response = await api.get(`/api/v1/superadmin/taxonomy/mappings/${subSectorId}`);
      setMappings(safeArray(response.data));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching mappings:', error);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleAddIndustry = async () => {
    try {
      // D18 — Edit uses the additive PUT (preserves stable id); Add uses POST.
      if (industryForm?.id) {
        await api.put(`/api/v1/superadmin/taxonomy/industries/${industryForm.id}`, industryForm);
      } else {
        await api.post('/api/v1/superadmin/taxonomy/industries', industryForm);
      }
      toast({
        title: 'Success',
        description: industryForm?.id ? 'Industry updated successfully' : 'Industry added successfully'
      });
      setShowAddIndustry(false);
      setIndustryForm({ name: '', description: '', icon: '' });
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || (industryForm?.id ? 'Failed to update industry' : 'Failed to add industry'),
        variant: 'destructive'
      });
    }
  };

  const handleAddSubSector = async () => {
    try {
      // D18 — Edit uses the additive PUT (preserves stable id; parent industry not reparented); Add uses POST.
      if (subSectorForm?.id) {
        await api.put(`/api/v1/superadmin/taxonomy/sub-sectors/${subSectorForm.id}`, subSectorForm);
      } else {
        await api.post('/api/v1/superadmin/taxonomy/sub-sectors', subSectorForm);
      }
      toast({
        title: 'Success',
        description: subSectorForm?.id ? 'Sub-sector updated successfully' : 'Sub-sector added successfully'
      });
      setShowAddSubSector(false);
      setSubSectorForm({ industry_id: '', name: '', description: '' });
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || (subSectorForm?.id ? 'Failed to update sub-sector' : 'Failed to add sub-sector'),
        variant: 'destructive'
      });
    }
  };

  const handleAddCategory = async () => {
    try {
      // Validate that sub-sectors are selected if not universal
      if (!categoryForm.is_universal && categoryForm.sub_sector_ids.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please select at least one sub-sector for this category',
          variant: 'destructive'
        });
        return;
      }

      const payload = {
        ...categoryForm,
        ai_intent_keywords: categoryForm.ai_intent_keywords
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k)
      };

      // Remove sub_sector_ids from payload as it's not part of the category model
      const subSectorIds = payload.sub_sector_ids;
      delete payload.sub_sector_ids;

      // Create the feedback category
      const categoryResp = await api.post('/api/v1/superadmin/taxonomy/feedback-categories', payload);
      const newCategoryId = categoryResp.data?.id;

      // If not universal, create mappings for selected sub-sectors
      if (!categoryForm.is_universal && subSectorIds.length > 0) {
        const mappingPromises = subSectorIds.map((subSectorId) =>
          api.post('/api/v1/superadmin/taxonomy/mappings', {
            sub_sector_id: subSectorId,
            feedback_category_id: newCategoryId,
            escalation_level: 'operator',
            sla_hours: 24,
            auto_priority: categoryForm.default_priority_level,
            is_mandatory: true
          })
        );
        await Promise.all(mappingPromises);
      }

      toast({
        title: 'Success',
        description: `Feedback category added successfully${
          subSectorIds.length > 0 ? ` and mapped to ${subSectorIds.length} sub-sector(s)` : ''
        }`
      });

      setShowAddCategory(false);
      setCategoryForm({
        name: '',
        description: '',
        default_priority_level: 'medium',
        ai_intent_keywords: '',
        is_universal: false,
        sub_sector_ids: []
      });
      fetchAllData();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding category:', error);
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || 'Failed to add category',
        variant: 'destructive'
      });
    }
  };

  const handleAddMapping = async () => {
    try {
      await api.post('/api/v1/superadmin/taxonomy/mappings', mappingForm);
      toast({
        title: 'Success',
        description: 'Mapping added successfully'
      });
      setShowAddMapping(false);
      setMappingForm({
        sub_sector_id: '',
        feedback_category_id: '',
        escalation_level: 'operator',
        sla_hours: 24,
        auto_priority: 'medium',
        is_mandatory: true
      });
      if (selectedSubSector) {
        fetchMappingsForSubSector(selectedSubSector.id);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || 'Failed to add mapping',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;

    try {
      await api.delete(`/api/v1/superadmin/taxonomy/mappings/${mappingId}`);
      toast({
        title: 'Success',
        description: 'Mapping deleted successfully'
      });
      if (selectedSubSector) {
        fetchMappingsForSubSector(selectedSubSector.id);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.response?.data?.detail || 'Failed to delete mapping',
        variant: 'destructive'
      });
    }
  };

  const toggleIndustry = (industryId) => {
    const newExpanded = new Set(expandedIndustries);
    if (newExpanded.has(industryId)) {
      newExpanded.delete(industryId);
    } else {
      newExpanded.add(industryId);
    }
    setExpandedIndustries(newExpanded);
  };

  const handleSelectSubSector = async (subSector) => {
    setSelectedSubSector(subSector);
    await fetchMappingsForSubSector(subSector.id);
    setActiveTab('mappings');
  };

  const getCategoryById = (categoryId) => {
    return feedbackCategories.find((c) => c.id === categoryId);
  };

  const getSubSectorsByIndustry = (industryId) => {
    return subSectors.filter((s) => s.industry_id === industryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading taxonomy data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback Taxonomy Manager</h1>
          <p className="text-gray-600 mt-1">Manage industries, sub-sectors, and feedback categories</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddIndustry(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Industry
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Tag className="h-4 w-4 mr-2" />
                Quick Add
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Quick Add</DialogTitle>
                <DialogDescription>Choose what to add to your taxonomy</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Button className="w-full justify-start" variant="outline" onClick={() => { setShowAddIndustry(true); }}>
                  <Building2 className="h-4 w-4 mr-2" />
                  New Industry
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => { setShowAddSubSector(true); }}>
                  <Settings className="h-4 w-4 mr-2" />
                  New Sub-Sector
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => { setShowAddCategory(true); }}>
                  <Tag className="h-4 w-4 mr-2" />
                  New Category
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Selected Industry Info Bar */}
      {selectedIndustry && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Selected: {selectedIndustry.name}</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIndustry(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Industries</p>
                <p className="text-3xl font-bold text-blue-600">{industries.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sub-Sectors</p>
                <p className="text-3xl font-bold text-purple-600">{subSectors.length}</p>
              </div>
              <Settings className="h-8 w-8 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categories</p>
                <p className="text-3xl font-bold text-green-600">{feedbackCategories.length}</p>
              </div>
              <Tag className="h-8 w-8 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Universal</p>
                <p className="text-3xl font-bold text-orange-600">
                  {feedbackCategories.filter((c) => c.is_universal).length}
                </p>
              </div>
              <Check className="h-8 w-8 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'overview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('industries')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'industries'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Industries & Sub-Sectors
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'categories'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Feedback Categories
        </button>
        {selectedSubSector && (
          <button
            onClick={() => setActiveTab('mappings')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'mappings'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mappings: {selectedSubSector.name}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Card>
            <CardHeader>
              <CardTitle>Taxonomy Overview</CardTitle>
              <CardDescription>Hierarchical view of all industries and sub-sectors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto pr-2 space-y-2">
                {industries.map((industry) => {
                  const isExpanded = expandedIndustries.has(industry.id);
                  const industrySubSectors = getSubSectorsByIndustry(industry.id);

                  return (
                    <div key={industry.id} className="border rounded-lg">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => { toggleIndustry(industry.id); setSelectedIndustry(industry); }}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-600" />
                          )}
                          <Building2 className="h-5 w-5 text-blue-600" />
                          <div>
                            <h3 className="font-semibold text-gray-900">{industry.name}</h3>
                            <p className="text-sm text-gray-600">{industry.description}</p>
                          </div>
                        </div>
                        <Badge variant="outline">{industrySubSectors.length} sub-sectors</Badge>
                      </div>

                      {isExpanded && (
                        <div className="border-t bg-gray-50 p-4 space-y-2">
                          {industrySubSectors.map((subSector) => (
                            <div
                              key={subSector.id}
                              className="flex items-center justify-between p-3 bg-white rounded border hover:border-blue-300 cursor-pointer"
                              onClick={() => handleSelectSubSector(subSector)}
                            >
                              <div className="flex items-center gap-3">
                                <Settings className="h-4 w-4 text-purple-600" />
                                <span className="font-medium text-gray-900">{subSector.name}</span>
                              </div>
                              <Button size="sm" variant="outline">
                                View Mappings
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSubSectorForm({ ...subSectorForm, industry_id: industry.id });
                              setShowAddSubSector(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Sub-Sector to {industry.name}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Industries & Sub-Sectors Tab */}
        {activeTab === 'industries' && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Industries & Sub-Sectors</CardTitle>
                  <CardDescription>Manage industry and sub-sector taxonomy</CardDescription>
                </div>
                <Button onClick={() => setShowAddIndustry(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Industry
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto pr-2 space-y-2">
                {industries.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No industries found</p>
                    <p className="text-sm text-gray-500 mt-1">Create your first industry to get started</p>
                  </div>
                ) : (
                  industries.map((industry) => {
                    const isExpanded = expandedIndustries.has(industry.id);
                    const industrySubSectors = getSubSectorsByIndustry(industry.id);

                    return (
                      <div key={industry.id} className="border rounded-lg">
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                          onClick={() => toggleIndustry(industry.id)}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-600" />
                            )}
                            <Building2 className="h-5 w-5 text-blue-600" />
                            <div>
                              <h3 className="font-semibold text-gray-900">{industry.name}</h3>
                              <p className="text-sm text-gray-600">{industry.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{industrySubSectors.length} sub-sectors</Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIndustryForm(industry);
                                setShowAddIndustry(true);
                              }}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t bg-gray-50 p-4 space-y-2">
                            {industrySubSectors.length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-4">
                                No sub-sectors yet. Add one to get started.
                              </p>
                            ) : (
                              industrySubSectors.map((subSector) => (
                                <div
                                  key={subSector.id}
                                  className="flex items-center justify-between p-3 bg-white rounded border hover:border-blue-300"
                                >
                                  <div className="flex items-center gap-3">
                                    <Settings className="h-4 w-4 text-purple-600" />
                                    <div>
                                      <span className="font-medium text-gray-900">{subSector.name}</span>
                                      <p className="text-xs text-gray-500">{subSector.description}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={() => handleSelectSubSector(subSector)}>
                                      View Mappings
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setSubSectorForm(subSector);
                                        setShowAddSubSector(true);
                                      }}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSubSectorForm({ ...subSectorForm, industry_id: industry.id });
                                setShowAddSubSector(true);
                              }}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Sub-Sector to {industry.name}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <Card>
            <CardHeader>
              <CardTitle>Feedback Categories</CardTitle>
              <CardDescription>Manage feedback categories - universal (apply to all) and sector-specific</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[600px] overflow-y-auto pr-2 space-y-4">
                {/* Universal Categories */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Check className="h-5 w-5 text-orange-600" />
                    Universal Categories ({feedbackCategories.filter((c) => c.is_universal).length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {feedbackCategories
                      .filter((cat) => cat.is_universal)
                      .map((category) => (
                        <div key={category.id} className="border rounded-lg p-4 hover:border-blue-300">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{category.name}</h4>
                                <Badge
                                  variant={
                                    category.default_priority_level === 'critical'
                                      ? 'destructive'
                                      : category.default_priority_level === 'high'
                                      ? 'default'
                                      : 'outline'
                                  }
                                >
                                  {category.default_priority_level}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                              <div className="flex flex-wrap gap-1">
                                {safeArray(category.ai_intent_keywords).slice(0, 3).map((keyword, idx) => (
                                  <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {keyword}
                                  </span>
                                ))}
                                {safeArray(category.ai_intent_keywords).length > 3 && (
                                  <span className="text-xs text-gray-500 px-2 py-1">
                                    +{safeArray(category.ai_intent_keywords).length - 3} more
                                  </span>
                                )}
                              </div>
                              {renderCatGov(category)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Sector-Specific Categories */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-purple-600" />
                    Sector-Specific Categories ({feedbackCategories.filter((c) => !c.is_universal).length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {feedbackCategories
                      .filter((cat) => !cat.is_universal)
                      .map((category) => (
                        <div key={category.id} className="border rounded-lg p-4 hover:border-blue-300">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-gray-900">{category.name}</h4>
                                <Badge
                                  variant={
                                    category.default_priority_level === 'critical'
                                      ? 'destructive'
                                      : category.default_priority_level === 'high'
                                      ? 'default'
                                      : 'outline'
                                  }
                                >
                                  {category.default_priority_level}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                              <div className="flex flex-wrap gap-1">
                                {safeArray(category.ai_intent_keywords).slice(0, 3).map((keyword, idx) => (
                                  <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                    {keyword}
                                  </span>
                                ))}
                                {safeArray(category.ai_intent_keywords).length > 3 && (
                                  <span className="text-xs text-gray-500 px-2 py-1">
                                    +{safeArray(category.ai_intent_keywords).length - 3} more
                                  </span>
                                )}
                              </div>
                              {renderCatGov(category)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mappings Tab */}
        {activeTab === 'mappings' && selectedSubSector && (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Mappings for {selectedSubSector.name}</CardTitle>
                  <CardDescription>Manage feedback category mappings for this sub-sector</CardDescription>
                </div>
                <Button onClick={() => setShowAddMapping(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Mapping
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {mappings.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No mappings found for this sub-sector</p>
                    <p className="text-sm text-gray-500 mt-1">Add mappings to connect categories to this sub-sector</p>
                  </div>
                ) : (
                  mappings.map((mapping) => {
                    const category = getCategoryById(mapping.feedback_category_id);
                    return (
                      <div key={mapping.id} className="border rounded-lg p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{category?.name || 'Unknown Category'}</h4>
                            <Badge variant="outline">{mapping.escalation_level}</Badge>
                            <Badge variant="outline">SLA: {mapping.sla_hours}h</Badge>
                            <Badge variant="outline">Auto: {mapping.auto_priority}</Badge>
                            {mapping.is_mandatory && <Badge variant="default">Mandatory</Badge>}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{category?.description}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteMapping(mapping.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Industry Dialog */}
      <Dialog open={showAddIndustry} onOpenChange={setShowAddIndustry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{industryForm?.id ? 'Edit Industry' : 'Add Industry'}</DialogTitle>
            <DialogDescription>Create or update an industry in your taxonomy</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Industry Name</label>
              <Input
                value={industryForm.name}
                onChange={(e) => setIndustryForm({ ...industryForm, name: e.target.value })}
                placeholder="e.g., Healthcare"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={industryForm.description}
                onChange={(e) => setIndustryForm({ ...industryForm, description: e.target.value })}
                placeholder="Brief description of the industry"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Icon (optional)</label>
              <Input
                value={industryForm.icon}
                onChange={(e) => setIndustryForm({ ...industryForm, icon: e.target.value })}
                placeholder="Icon name or URL"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddIndustry(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddIndustry} className="bg-blue-600 hover:bg-blue-700">
                <Save className="h-4 w-4 mr-2" />
                Save Industry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Sub-Sector Dialog */}
      <Dialog open={showAddSubSector} onOpenChange={setShowAddSubSector}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sub-Sector</DialogTitle>
            <DialogDescription>Create a sub-sector under an industry</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Industry</label>
              <Select
                value={subSectorForm.industry_id}
                onValueChange={(value) => setSubSectorForm({ ...subSectorForm, industry_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry.id} value={industry.id}>
                      {industry.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Sub-Sector Name</label>
              <Input
                value={subSectorForm.name}
                onChange={(e) => setSubSectorForm({ ...subSectorForm, name: e.target.value })}
                placeholder="e.g., Hospitals"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={subSectorForm.description}
                onChange={(e) => setSubSectorForm({ ...subSectorForm, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddSubSector(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddSubSector} className="bg-purple-600 hover:bg-purple-700">
                <Save className="h-4 w-4 mr-2" />
                Save Sub-Sector
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Feedback Category</DialogTitle>
            <DialogDescription>Create a universal or sector-specific feedback category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Category Name</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Billing Issues"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Describe what this category covers"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Default Priority</label>
              <Select
                value={categoryForm.default_priority_level}
                onValueChange={(value) => setCategoryForm({ ...categoryForm, default_priority_level: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">low</SelectItem>
                  <SelectItem value="medium">medium</SelectItem>
                  <SelectItem value="high">high</SelectItem>
                  <SelectItem value="critical">critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">AI Intent Keywords (comma-separated)</label>
              <Input
                value={categoryForm.ai_intent_keywords}
                onChange={(e) => setCategoryForm({ ...categoryForm, ai_intent_keywords: e.target.value })}
                placeholder="e.g., invoice, charge, refund"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is_universal"
                type="checkbox"
                checked={categoryForm.is_universal}
                onChange={(e) => setCategoryForm({ ...categoryForm, is_universal: e.target.checked })}
              />
              <label htmlFor="is_universal" className="text-sm font-medium">
                Universal category (applies to all industries/sub-sectors)
              </label>
            </div>

            {!categoryForm.is_universal && (
              <div>
                <label className="text-sm font-medium">Select Sub-Sectors (at least one)</label>
                <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                  {subSectors.map((s) => {
                    const checked = categoryForm.sub_sector_ids.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCategoryForm({
                                ...categoryForm,
                                sub_sector_ids: [...categoryForm.sub_sector_ids, s.id]
                              });
                            } else {
                              setCategoryForm({
                                ...categoryForm,
                                sub_sector_ids: categoryForm.sub_sector_ids.filter((id) => id !== s.id)
                              });
                            }
                          }}
                        />
                        <span>{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddCategory(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddCategory} className="bg-green-600 hover:bg-green-700">
                <Save className="h-4 w-4 mr-2" />
                Save Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Mapping Dialog */}
      <Dialog open={showAddMapping} onOpenChange={setShowAddMapping}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Mapping</DialogTitle>
            <DialogDescription>Map a feedback category to the selected sub-sector</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Feedback Category</label>
              <Select
                value={mappingForm.feedback_category_id}
                onValueChange={(value) => setMappingForm({ ...mappingForm, feedback_category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {feedbackCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Escalation Level</label>
              <Select
                value={mappingForm.escalation_level}
                onValueChange={(value) => setMappingForm({ ...mappingForm, escalation_level: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select escalation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">operator</SelectItem>
                  <SelectItem value="supervisor">supervisor</SelectItem>
                  <SelectItem value="manager">manager</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">SLA Hours</label>
              <Input
                type="number"
                value={mappingForm.sla_hours}
                onChange={(e) => setMappingForm({ ...mappingForm, sla_hours: Number(e.target.value) })}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Auto Priority</label>
              <Select
                value={mappingForm.auto_priority}
                onValueChange={(value) => setMappingForm({ ...mappingForm, auto_priority: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">low</SelectItem>
                  <SelectItem value="medium">medium</SelectItem>
                  <SelectItem value="high">high</SelectItem>
                  <SelectItem value="critical">critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is_mandatory"
                type="checkbox"
                checked={mappingForm.is_mandatory}
                onChange={(e) => setMappingForm({ ...mappingForm, is_mandatory: e.target.checked })}
              />
              <label htmlFor="is_mandatory" className="text-sm font-medium">
                Mandatory mapping
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddMapping(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddMapping} className="bg-purple-600 hover:bg-purple-700">
                <Save className="h-4 w-4 mr-2" />
                Save Mapping
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* TAX-GOV-2 Slice 2b — feedback category metadata Edit (distinct from lifecycle/propagation) */}
      <Dialog open={editCat.open} onOpenChange={(o) => !o && setEditCat((s) => ({ ...s, open: false }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit feedback category</DialogTitle>
            <DialogDescription>Edit global category metadata. This does not change tenant-local settings; applicability changes propagate via governed reconciliation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input data-testid="edit-name" value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea data-testid="edit-desc" value={editCat.description} onChange={(e) => setEditCat({ ...editCat, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Default priority</label>
              <Select value={editCat.default_priority_level} onValueChange={(v) => setEditCat({ ...editCat, default_priority_level: v })}>
                <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">low</SelectItem>
                  <SelectItem value="medium">medium</SelectItem>
                  <SelectItem value="high">high</SelectItem>
                  <SelectItem value="critical">critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">AI intent keywords (comma-separated)</label>
              <Input data-testid="edit-keywords" value={editCat.ai_intent_keywords} onChange={(e) => setEditCat({ ...editCat, ai_intent_keywords: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <input id="edit_is_universal" type="checkbox" checked={editCat.is_universal} onChange={(e) => setEditCat({ ...editCat, is_universal: e.target.checked })} />
              <label htmlFor="edit_is_universal" className="text-sm font-medium">Universal (applies to all sectors)</label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditCat((s) => ({ ...s, open: false }))}>Cancel</Button>
              <Button onClick={saveEditCategory} disabled={editCat.saving || !editCat.name.trim()} data-testid="edit-save">
                <Save className="h-4 w-4 mr-2" />{editCat.saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* TAX-GOV-2 Slice 2b — governed propagation / reconciliation preview → apply */}
      <Dialog open={gov.open} onOpenChange={(o) => !o && setGov({ open: false, category: null, kind: null, loading: false, plan: null, applying: false, result: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="gov-title">
              {gov.kind === 'propagation' ? 'Propagation preview' : 'Reconciliation preview'}
              {gov.category ? ` — ${gov.category.name}` : ''}
            </DialogTitle>
            <DialogDescription>
              Review the governed plan below, then Apply. Tenant-local settings (enable/disable, custom priority/SLA/keywords)
              are always preserved; deactivation never deletes tenant data or history.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3" data-testid="gov-body">
            {gov.loading && <p className="text-sm text-gray-600">Computing plan…</p>}
            {!gov.loading && gov.plan && gov.plan.error && (
              <p className="text-sm text-red-600" data-testid="gov-error">{String(gov.plan.error)}</p>
            )}
            {!gov.loading && gov.plan && !gov.plan.error && (
              <div className="text-sm space-y-1" data-testid="gov-plan">
                <div>Affected tenants: <b>{gov.plan.affected_tenants ?? '—'}</b></div>
                {gov.kind === 'propagation' ? (
                  <>
                    <div>Already current: <b>{gov.plan.already_current ?? '—'}</b></div>
                    <div>Would add: <b data-testid="gov-would-add">{gov.plan.would_add ?? 0}</b></div>
                    {gov.plan.would_skip_inactive && <div className="text-amber-600">Category is inactive — nothing will be added.</div>}
                  </>
                ) : (
                  <>
                    <div>Would mark active: <b>{gov.plan.would_mark_active ?? 0}</b></div>
                    <div>Would mark inactive: <b data-testid="gov-would-inactive">{gov.plan.would_mark_inactive ?? 0}</b></div>
                    <div>Would add: <b>{gov.plan.would_add ?? 0}</b></div>
                    <div className="text-gray-500">Already current: {gov.plan.already_current ?? 0} · Already inactive: {gov.plan.already_inactive ?? 0}</div>
                    <div className="text-green-700">Hard deletes: {gov.plan.hard_delete ?? 0} (tenant rows & history preserved)</div>
                  </>
                )}
                <div className="text-xs text-gray-400 break-all">plan hash: {gov.plan.dry_run_hash || '—'}</div>
              </div>
            )}
            {gov.result && (
              <div
                className={`text-sm mt-2 p-2 rounded ${gov.result.result === 'SUCCESS' ? 'bg-green-50 text-green-700' : gov.result.result === 'PARTIAL' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}
                data-testid="gov-result"
              >
                Result: <b>{gov.result.result || 'FAILED'}</b>
                {gov.kind === 'propagation'
                  ? ` — added ${gov.result.added ?? 0}, skipped ${gov.result.skipped ?? 0}, failed ${gov.result.failed ?? 0}`
                  : ` — added ${gov.result.added ?? 0}, reactivated ${gov.result.reactivated ?? 0}, deactivated ${gov.result.deactivated ?? 0}, failed ${gov.result.failed ?? 0}`}
                {gov.result.reason ? ` (${gov.result.reason})` : ''}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setGov({ open: false, category: null, kind: null, loading: false, plan: null, applying: false, result: null })}>
              Close
            </Button>
            <Button
              onClick={applyGovPlan}
              disabled={gov.loading || gov.applying || !gov.plan || !gov.plan.dry_run_hash || !!gov.result}
              data-testid="gov-apply"
            >
              {gov.applying ? 'Applying…' : 'Apply reviewed plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default TaxonomyManager;